/**
 * 左上にある間取り図を作るファイル
 *
 * webglフォルダの中にあるがこの間取り図は単独のcanvasで作られる
 */

import { Vector2, Vector3 } from "three";

import store from "@/store";

const CNVS_SIZE = new Vector2(512, 512);
const BASEIMG_SIZE = new Vector2(0, 0);

const PICICON_R = 10;

const cameraDirection = new Vector3();

export default class MiniMap {
  constructor() {
    this.display = false;
    this.isMadoriMode = false;
    this.baseImage = null;
    this.center = {
      x: 0,
      y: 0
    };
    this.size = {
      x: 0,
      y: 0
    };

    this.canvas = document.createElement("canvas");
    this.canvas.width = CNVS_SIZE.x;
    this.canvas.height = CNVS_SIZE.y;

    this.ctx = this.canvas.getContext("2d");

    this.pinImg = new Image();
    this.pinImg.src = `${process.env.BASE_URL}img/icon/pin_place.svg`;

    this.picIconData = null;
    this.nodeData = null;
    this.floorSize = null;

    // 1人称のカメラ位置を保持しておく
    this.firstPersonCameraPos = new Vector2(0, 0);
  }

  init(mode, image, center) {
    if (!image || !center) {
      this.display = false;
      return;
    }

    this.cameraMode = mode;

    this.display = true;
    this.baseImage = image;
    this.center.x = center.x;
    this.center.y = center.y;

    BASEIMG_SIZE.set(this.baseImage.width, this.baseImage.height);
  }

  setPicIcon(data) {
    this.picIconData = data;
  }

  setNode(data) {
    this.nodeData = data;
  }

  setFloorSize(data) {
    this.floorSize = data;
  }

  update() {
    this.draw();
  }

  draw() {
    if (!this.display) return;

    this.drawMap();
    this.drawNode();
    this.drawPin();
    // this.drawPicIcon();
  }

  drawMap() {
    // clear
    this.ctx.fillStyle = "#fff";
    this.ctx.fillRect(0, 0, CNVS_SIZE.x, CNVS_SIZE.y);

    let scale = 1;
    if (store.state.webgl.camera.mode === "thirdPerson") {
      const sizeMax = Math.max(this.floorSize.x, this.floorSize.y);
      scale = 512 / sizeMax;
    }

    this.ctx.drawImage(
      this.baseImage,
      0,
      0,
      CNVS_SIZE.x,
      CNVS_SIZE.y,
      256 - this.center.x * scale,
      256 - this.center.y * scale,
      CNVS_SIZE.x * scale,
      CNVS_SIZE.y * scale
    );
  }

  drawNode() {
    if (store.state.webgl.camera.mode === "thirdPerson") return;

    for (let i = 0; i < this.nodeData.length; i++) {
      const n = this.nodeData[i];
      if (n.next_id !== null) {
        const nCP = { x: n.x + 256, y: n.y + 256 };

        this.ctx.save();
        this.ctx.translate(nCP.x, nCP.y);

        this.ctx.fillStyle = "#fff";
        this.ctx.beginPath();
        this.ctx.arc(0, 0, 16, 0, Math.PI * 2, false);
        this.ctx.fill();

        this.ctx.fillStyle = "#ffad0b";
        this.ctx.beginPath();
        this.ctx.arc(0, 0, 13, 0, Math.PI * 2, false);
        this.ctx.fill();

        this.ctx.translate(-nCP.x, -nCP.y);
        this.ctx.restore();
      }
    }
  }

  drawPin() {
    if (store.state.webgl.camera.mode === "thirdPerson") return;

    const pos = this.getCameraPositionToCnvsPosition();
    const theta = this.getTheta();
    const scale = this.isMadoriMode ? 1 : 2;

    this.ctx.save();
    this.ctx.translate(pos.x, pos.y);
    this.ctx.rotate(theta - Math.PI * 0.5);

    this.ctx.drawImage(
      this.pinImg,
      -14 * scale,
      -14 * scale,
      30 * scale,
      39 * scale
    );

    this.ctx.translate(-pos.x, -pos.y);
    this.ctx.restore();
  }

  drawPicIcon() {
    for (let i = 0; i < this.picIconData.length; i++) {
      const pos = this.picIconData[i].position;
      const x = (pos.x / 512 + 0.5) * this.center.x;
      const y = (pos.y / 512 + 0.5) * this.center.y;
      this.ctx.fillStyle = "#00ff00";
      this.ctx.beginPath();
      this.ctx.arc(x, y, PICICON_R, 0, Math.PI * 2, false);
      this.ctx.fill();
    }
  }

  getCameraPositionToCnvsPosition() {
    const { meterPerPixel, camera } = store.state.webgl;
    const cPX = camera.position.x / meterPerPixel + 256;
    const cPY = camera.position.z / meterPerPixel + 256;
    return { x: cPX, y: cPY };
  }

  getTheta() {
    const { camera } = store.state.webgl;
    if (this.cameraMode === "firstPerson") {
      camera.getWorldDirection(cameraDirection);
    }

    let theta = Math.atan(cameraDirection.z / cameraDirection.x);
    if (cameraDirection.x <= 0) theta += Math.PI;
    return theta;
  }

  getNodePositionToCnvsPosition(nX, nY) {
    const { meterPerPixel, camera } = store.state.webgl;

    const floorWidth = camera.floorSize.x * meterPerPixel;
    const floorHeight = camera.floorSize.y * meterPerPixel;

    const cPX = nX + floorWidth * 0.5;
    const cPY = nY + floorHeight * 0.5;

    const x = (cPX / floorWidth) * this.center.x * 2;
    const y = (cPY / floorHeight) * this.center.y * 2;
    return { x, y };
  }

  changeCameraMode(mode) {
    this.cameraMode = mode;

    const { camera } = store.state.webgl;
    if (mode === "thirdPerson") {
      this.firstPersonCameraPos.set(camera.position.x, camera.position.z);
    }
  }
}
